import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Colors, Spacing } from '_styles';
import { SESSION_MAX_DURATION_MS, SESSION_HEARTBEAT_MS, AIRPORTS_SUPPORTS_SESSIONS, TIMELINE_V2_AIRPORTS } from '_utils/constants';

// navigation
import { useHistory, useLocation } from "react-router-dom";
import { useIsMobile } from '_utils/hooks';
import { NavLink } from "react-router-dom";

// sidebar library
import { CDBSidebar, CDBSidebarHeader, CDBSidebarMenuItem, CDBSidebarContent, CDBSidebarMenu, CDBSidebarFooter } from "cdbreact";

// icons
import { SidebarIcon } from "_molecules";
import { FiLogOut, FiMenu } from "react-icons/fi";
import logo from "_assets/images/logo/white-logo-text.png";

//redux
import { useSelector, useDispatch } from "react-redux";
import {
  logout,
  getDbInfo,
  selectFirebaseUser,
  selectUserDb,
  reset,
  resetStatus,
  selectTravelAgent,
} from "_redux/slices/main-screen/user"
import { onSnapshot, collection, query, where } from 'firebase/firestore';
import { firestore } from '_utils/firebase-app';


import { reset as HistoryReset } from "_redux/slices/assistance-bookings/bookings-history";
import { reset as TableReset } from "_redux/slices/prm-bookings/bookings-table";
import { reset as BookingsReset } from "_redux/slices/prm-bookings/bookings";
import { reset as CalendarReset } from "_redux/slices/prm-bookings/calendar";
import { reset as ScreenReset } from "_redux/slices/main-screen/render-screen";
import { reset as ReviewsReset } from "_redux/slices/analytics/reviews";
import { reset as RewardsReset } from "_redux/slices/prm-club/rewards";
import { reset as UserBookingsReset } from "_redux/slices/website/userbookings";
import { reset as resetWeather } from "_redux/slices/analytics/weather";
//timeline reducers
import { reset as EmployeeReset } from '_redux/slices/timeline/employees';
import { reset as FlightScannerReset } from '_redux/slices/timeline/flight-scanner';
import { reset as SelectedTaskReset } from '_redux/slices/timeline/selected-task';
import { reset as TimelineAreaReset } from '_redux/slices/timeline/timeline-areas';
import { reset as TimelineDataReset } from '_redux/slices/timeline/timeline-data';
import { reset as resetTimeLineControl } from "_redux/slices/timeline/timeline-control";
import { reset as resetTimeLineDashboard } from "_redux/slices/timeline/timeline-dashboard";
import { getAllSupportDesks, selectUnreadChatsCount, setUnreadChat, readAllChats } from "_redux/slices/messaging/messaging";
import { closePortalSession, openPortalSession, sendSessionHeartbeat, getRemainingSessionMs } from '_services/session-service';


// Incident statuses that still need attention: new (open) + acknowledged-pending-approval.
// 'closed' means approved & complete, so it is excluded from the sidebar badge count.
const INCIDENT_NEEDS_ACTION_STATUSES = ['open', 'in_progress', 'fully_acknowledged'];

const Sidebar = (props) => {

  //#region redux
  const dispatch = useDispatch();
  const firebaseUser = useSelector(selectFirebaseUser);
  const travelAgent = useSelector(selectTravelAgent);
  const userDb = useSelector(selectUserDb);
  const unreadChatsCount = useSelector(selectUnreadChatsCount)
  //#endregion

  // Count of incident reports awaiting acknowledgement / approval, shown as a sidebar badge.
  const [incidentActionCount, setIncidentActionCount] = useState(0);

  const bookingsFeedbackOnly = userDb?.role === "BOOKINGS_FEEDBACK";
  const securityOnly = userDb?.role === "SECURITY";
  const lanyardDeskOnly = userDb?.role === "LANYARD_ORDERS_DESK";
  const airlineDeskOnly = userDb?.role === "AIRLINE_PAX_DESK";
  // desk logins have no airport inbox or incident reports
  const paxDeskOnly = userDb?.role === "PAX_REQUESTS_DESK" || lanyardDeskOnly || airlineDeskOnly;

  // states

  const [retailer, setRetailer] = useState(false)
  const [showTimeoutModal, setShowTimeoutModal] = useState(false)
  const sessionTimeoutRef = useRef(null)

  // nav
  const history = useHistory();
  const location = useLocation();

  // phones: the sidebar becomes a slide-out drawer opened from a top bar hamburger.
  // It stays mounted while closed — it owns the session timers and the badge listeners.
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  // any navigation lands with the drawer shut
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // escape closes the drawer; the page behind it doesn't scroll while it's open
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  // handle icon press
  const handlePress = (page) => {
    history.push("/" + page);
  };

  // logout
  const handleLogout = async (closedBy = "user") => {
    // logout and change page
    await closePortalSession(closedBy);
    dispatch(logout());
    history.push("/login");

    // reset all slices
    dispatch(resetStatus());
    dispatch(reset());
    dispatch(HistoryReset());
    dispatch(TableReset());
    dispatch(BookingsReset());
    dispatch(CalendarReset());
    dispatch(ScreenReset());
    dispatch(ReviewsReset());
    dispatch(RewardsReset());
    dispatch(UserBookingsReset());
    dispatch(resetWeather());
    dispatch(EmployeeReset());
    dispatch(FlightScannerReset());
    dispatch(SelectedTaskReset());
    dispatch(TimelineAreaReset());
    dispatch(TimelineDataReset());
    dispatch(resetTimeLineControl());
    dispatch(resetTimeLineDashboard());
    dispatch(readAllChats())
  };


  const handleTimeoutCase = () => {
    setShowTimeoutModal(true);
    handleLogout('timeout');
  }

  useEffect(() => {
    dispatch(readAllChats())
  }, [])

  useEffect(() => {
    if (!userDb?.details?.authority) return;
    // the passenger requests desk has no airport inbox
    if (paxDeskOnly) return;
    const iata = userDb.details.authority;

    const q = query(
      collection(firestore, 'conversations'),
      where('portalUser', '==', iata)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let hasUnreadCount = 0;

      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();

        if (data.notification === true && !data.archived && data.lastAction != undefined) {
          hasUnreadCount += 1;
        }

      });

      if (hasUnreadCount) {
        dispatch(setUnreadChat(hasUnreadCount));
      } else {
        dispatch(setUnreadChat(0)); // or reset when all are read
      }
    });

    return () => unsubscribe();
  }, [userDb]);

  // Live count of incident reports still needing action (new + pending approval).
  // Filtered client-side by status to avoid a composite (iata + status) index.
  useEffect(() => {
    if (!userDb?.details?.authority) return;
    // the passenger requests desk has no incident reports
    if (paxDeskOnly) return;
    const iata = userDb.details.authority;

    const q = query(
      collection(firestore, 'incidentReports'),
      where('iata', '==', iata)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let count = 0;
      snapshot.forEach((doc) => {
        if (INCIDENT_NEEDS_ACTION_STATUSES.includes(doc.data().status)) count += 1;
      });
      setIncidentActionCount(count);
    });

    return () => unsubscribe();
  }, [userDb]);


  useEffect(() => {
    const interval = setInterval(async () => {
      const result = await sendSessionHeartbeat();
      if (result?.success === false && result?.reason === 'already_closed') {
        clearInterval(interval);
        handleTimeoutCase();
      }
    }, SESSION_HEARTBEAT_MS);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the portal session BEFORE the absolute-timeout effect below reads the
  // anchor. Effects run in declaration order, and openPortalSession clears a stale
  // anchor synchronously (before it awaits the POST). With the order reversed, a
  // fresh sign-in on a tablet still holding the previous shift's expired anchor was
  // read as "already past 12h30m" and logged straight back out — the repeated-kick
  // loop reported 7-8 Sep 2026. Keep this effect above that one.
  useEffect(() => {
    if (!userDb || !firebaseUser) return;
    if (!AIRPORTS_SUPPORTS_SESSIONS.includes(userDb?.details?.authority)) return;
    openPortalSession({
      userId: firebaseUser.uid,
      email: firebaseUser.email,
      role: userDb.role || userDb.position || "Not Provided",
      airport: userDb.details.authority,
      title: firebaseUser.displayName || "Not Provided",
      type: 'portal',
    });
  }, [userDb]); // eslint-disable-line react-hooks/exhaustive-deps

  // Absolute session timeout: force logout 12h 30m after login regardless of activity.
  // Survives page refresh by reading the stored login timestamp. Anchored to the
  // portal session's loginTime — the same anchor navigations/index.js schedules from,
  // so the two enforcers cannot disagree about when the wall falls.
  useEffect(() => {
    if (!AIRPORTS_SUPPORTS_SESSIONS.includes(userDb?.details?.authority)) return;
    const remaining = getRemainingSessionMs(SESSION_MAX_DURATION_MS, firebaseUser?.uid);
    if (remaining === null) return;
    if (remaining <= 0) {
      handleTimeoutCase();
      return;
    }
    clearTimeout(sessionTimeoutRef.current);
    sessionTimeoutRef.current = setTimeout(handleTimeoutCase, remaining);
    return () => clearTimeout(sessionTimeoutRef.current);
  }, [userDb]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userDb == null || (userDb == undefined && firebaseUser)) {
      dispatch(getDbInfo(firebaseUser.uid));
      getAllSupportDesks();
    }
    if (userDb && userDb.details && userDb.details.type == 'RETAILER') {
      setRetailer(true)
    }
  }, [userDb]);

  return (
    <>
    {/* phone top bar: hamburger + logo, in place of the sidebar column */}
    {isMobile && (
      <div style={mobileStyles.topBar}>
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={drawerOpen}
          aria-controls="portal-sidebar"
          onClick={() => setDrawerOpen(true)}
          style={mobileStyles.menuButton}
        >
          <FiMenu size={26} color="white" />
        </button>
        <a href="/calendar" className="text-decoration-none" style={{ display: 'flex' }}>
          <img src={logo} alt="PRM Assist" style={{ height: 26, width: 'auto' }} />
        </a>
      </div>
    )}

    {/* phone backdrop: tap outside the drawer to close it */}
    {isMobile && drawerOpen && <div aria-hidden onClick={closeDrawer} style={mobileStyles.backdrop} />}

    <div
      id="portal-sidebar"
      style={isMobile
        ? { ...mobileStyles.drawer, transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)', visibility: drawerOpen ? 'visible' : 'hidden' }
        : { display: 'flex', height: '100vh', overflow: 'scroll initial', position: '-webkit-sticky', position: 'sticky', top: 0 }}
    >
      {/* collapsed icon rail on desktop; fully expanded with labels inside the phone drawer.
          CDBSidebarContent collapses the sidebar on mount whenever its width (starts at 0) is below
          `breakpoint`, so the drawer needs a breakpoint it can never hit. Its state is only read on
          mount, hence the key to remount when crossing the phone breakpoint */}
      <CDBSidebar
        key={isMobile ? "mobile" : "desktop"}
        textColor="#fff"
        backgroundColor={Colors.SIDEBAR_COLOR}
        toggled={!isMobile}
        breakpoint={isMobile ? -1 : undefined}
      >

        {/* Header */}
        <CDBSidebarHeader
          prefix={isMobile
            // close button; stopPropagation keeps the library from collapsing the drawer to icons
            ? <i role="button" aria-label="Close menu" style={{ marginTop: Spacing.SCALE_8, padding: 6 }} className="fa fa-times" onClick={(e) => { e.stopPropagation(); closeDrawer(); }} />
            : <i style={{ marginTop: Spacing.SCALE_8 }} className="fa fa-bars" />}
        >
          <a
            href="/calendar"
            className="text-decoration-none"
            style={{ color: "inherit" }}
          >
            <img src={logo} style={{ width: "90%", height: "auto" }} />
          </a>
        </CDBSidebarHeader>

        {/* Content */}
        <CDBSidebarContent className="sidebar-content">
          <CDBSidebarMenu>
            {lanyardDeskOnly ? (
              <>
                <NavLink
                  onClick={() => {
                    handlePress("lanyard-orders");
                  }}
                  exact
                  to="/lanyard-orders"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="list">Lanyard Orders</CDBSidebarMenuItem>
                </NavLink>
              </>
            ) : airlineDeskOnly ? (
              <>
                <NavLink
                  onClick={() => {
                    handlePress("pax-requests");
                  }}
                  exact
                  to="/pax-requests"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="list">Passenger Requests</CDBSidebarMenuItem>
                </NavLink>
              </>
            ) : paxDeskOnly ? (
              <>
                <NavLink
                  onClick={() => {
                    handlePress("pax-requests");
                  }}
                  exact
                  to="/pax-requests"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="list">Passenger Requests</CDBSidebarMenuItem>
                </NavLink>
                <NavLink
                  onClick={() => {
                    handlePress("old-app-bookings");
                  }}
                  exact
                  to="/old-app-bookings"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="history">Old App Bookings</CDBSidebarMenuItem>
                </NavLink>
              </>
            ) : securityOnly ? (
              <>
                <NavLink
                  onClick={() => {
                    handlePress("prm-bookings");
                  }}
                  exact
                  to="/prm-bookings"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="list">Bookings</CDBSidebarMenuItem>
                </NavLink>
                <NavLink
                  onClick={() => {
                    handlePress("account");
                  }}
                  exact
                  to="/account"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="user">Account</CDBSidebarMenuItem>
                </NavLink>
              </>
            ) : bookingsFeedbackOnly ? (
              <>
                <NavLink exact to="/prm-calendar" activeClassName="activeClicked">
                  <CDBSidebarMenuItem icon="calendar">Calendar</CDBSidebarMenuItem>
                </NavLink>
                <NavLink
                  onClick={() => {
                    handlePress("create-booking");
                  }}
                  exact
                  to="/create-booking"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="plus">Create Booking</CDBSidebarMenuItem>
                </NavLink>
                <NavLink
                  onClick={() => {
                    handlePress("feedback");
                  }}
                  exact
                  to="/feedback"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="envelope">Feedback</CDBSidebarMenuItem>
                </NavLink>
                <NavLink
                  onClick={() => {
                    handlePress("account");
                  }}
                  exact
                  to="/account"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="user">Account</CDBSidebarMenuItem>
                </NavLink>
              </>
            ) : (
              <>
                {/* Calendar */}
                <NavLink exact to="/prm-calendar" activeClassName="activeClicked">
                  {userDb &&
                    userDb != null &&
                    userDb != undefined &&
                    userDb.role &&
                    userDb.role != undefined && !(userDb.details.type === "AIRLINE" && userDb.details.authority === "ALL") ? (
                    <CDBSidebarMenuItem icon="calendar">
                      Calendar
                    </CDBSidebarMenuItem>
                  ) : (
                    ""
                  )}
                </NavLink>

                {/* Assistance Bookings */}
                <NavLink
                  onClick={() => {
                    handlePress("assistance-bookings");
                  }}
                  exact
                  to="/assistance-bookings"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="plus">Bookings</CDBSidebarMenuItem>
                </NavLink>

                <NavLink onClick={() => { handlePress(TIMELINE_V2_AIRPORTS.includes(userDb.details.authority) ? 'timeline-v2' : 'timeline') }} exact to={TIMELINE_V2_AIRPORTS.includes(userDb.details.authority) ? "/timeline-v2" : "/timeline"} activeClassName="activeClicked">
                  {userDb && userDb != null && userDb != undefined && userDb.role && userDb.role != undefined && userDb.details.type === "AIRPORT" &&
                    <CDBSidebarMenuItem icon="clock">Timeline</CDBSidebarMenuItem>
                  }
                </NavLink>

                {/* Feedback */}
                <NavLink
                  onClick={() => {
                    handlePress("feedback");
                  }}
                  exact
                  to="/feedback"
                  activeClassName="activeClicked"
                >
                  {travelAgent || retailer || (userDb.details.type === "AIRLINE" && userDb.details.authority === "ALL") ? (
                    ""
                  ) : (
                    <CDBSidebarMenuItem icon="envelope">
                      Feedback
                    </CDBSidebarMenuItem>
                  )}
                </NavLink>

                {/* Support Desk */}
                <NavLink
                  onClick={() => {
                    handlePress("support-desk");
                  }}
                  exact
                  to="/support-desk"
                  activeClassName="activeClicked"
                >
                  {userDb &&
                    userDb != undefined &&
                    userDb != null &&
                    userDb.details &&
                    userDb.details != undefined &&
                    userDb.details.authority &&
                    userDb.details.authority != undefined && (
                      <CDBSidebarMenuItem icon="desktop">
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          Support Desk
                          {unreadChatsCount > 0 && (
                            <span
                              style={{
                                backgroundColor: "red",
                                borderRadius: "30%",
                                color: "white",
                                padding: "2px 6px",
                                fontSize: "16px",
                                marginLeft: "4px",
                                minWidth: "18px",
                                textAlign: "center",
                                display: "inline-block",
                              }}
                            >
                              {unreadChatsCount}
                            </span>
                          )}
                        </span>
                      </CDBSidebarMenuItem>
                    )}
                </NavLink>

                {/* Incident Reports  */}
                <NavLink
                  onClick={() => { handlePress('incident-reports'); }}
                  exact
                  to="/incident-reports"
                  activeClassName="activeClicked"
                >
                  {userDb &&
                    userDb != undefined &&
                    userDb != null &&
                    userDb.details &&
                    userDb.details.authority &&
                    userDb.details.authority != undefined && (
                      <CDBSidebarMenuItem icon="flag">
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          Incident Reports
                          {incidentActionCount > 0 && (
                            <span
                              style={{
                                backgroundColor: "red",
                                borderRadius: "30%",
                                color: "white",
                                padding: "2px 6px",
                                fontSize: "16px",
                                marginLeft: "4px",
                                minWidth: "18px",
                                textAlign: "center",
                                display: "inline-block",
                              }}
                            >
                              {incidentActionCount}
                            </span>
                          )}
                        </span>
                      </CDBSidebarMenuItem>
                    )}
                </NavLink>


                {/* Archive */}
                <NavLink
                  onClick={() => { handlePress('archive'); }}
                  exact
                  to="/archive"
                  activeClassName="activeClicked"
                >
                  {userDb &&
                    userDb != undefined &&
                    userDb != null &&
                    userDb.details &&
                    userDb.details.authority &&
                    userDb.details.authority != undefined && (
                      <CDBSidebarMenuItem icon="archive">
                        Archive
                      </CDBSidebarMenuItem>
                    )}
                </NavLink>

                {/* Global SLA Card */}
                {
                  <NavLink
                    onClick={() => {
                      handlePress("global-sla");
                    }}
                    exact
                    to="/global-sla"
                    activeClassName="activeClicked"
                  >
                    {travelAgent || retailer ||
                      (userDb &&
                        userDb != undefined &&
                        userDb != null &&
                        userDb.details &&
                        userDb.details != undefined &&
                        userDb.details.type &&
                        userDb.details.type != undefined &&
                        userDb.details.type == "AIRLINE") ? (
                      ""
                    ) : (
                      <CDBSidebarMenuItem icon="globe">Airports</CDBSidebarMenuItem>
                    )}
                  </NavLink>
                }

                {/* Marketplace */}
                {/* <NavLink onClick={()=>{handlePress('support-desk')}} exact to="/support-desk" activeClassName="activeClicked">
              <CDBSidebarMenuItem icon="desktop">Support Desk</CDBSidebarMenuItem>
            </NavLink> */}

                {/* Marketplace */}
                {/* Temp hardcoded condition to display marketplace only for Sarah */}
                <NavLink
                  onClick={() => {
                    handlePress("marketplace");
                  }}
                  exact
                  to="/marketplace"
                  activeClassName="activeClicked"
                >
                  {firebaseUser &&

                    firebaseUser.email === "sarah.mulreany@prmassist.com" ? (
                    <CDBSidebarMenuItem icon="shopping-cart">
                      Marketplace
                    </CDBSidebarMenuItem>
                  ) : (
                    ""
                  )}
                  {/* {userDb &&

                    userDb.role !== "GARETH" ? (
                    <CDBSidebarMenuItem icon="shopping-cart">
                      Marketplace
                    </CDBSidebarMenuItem>
                  ) : (
                    ""
                  )} */}
                </NavLink>

                {/* Account */}
                <a
                  href={"https://connect.prmassist.com/"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {!retailer && <CDBSidebarMenuItem icon="book">Connect</CDBSidebarMenuItem>}
                </a>

                <NavLink onClick={() => { handlePress('total-reports') }} exact to="/total-reports" activeClassName="activeClicked">
                  {userDb && userDb != null && userDb != undefined && userDb.role && userDb.role != undefined && (userDb.details.type === "AIRPORT" || (userDb.details.type === "AIRLINE" && userDb.details.authority === "ALL")) &&
                    <CDBSidebarMenuItem icon="file">Reports</CDBSidebarMenuItem>
                  }
                </NavLink>

                {/* Account */}
                <NavLink
                  onClick={() => {
                    handlePress("account");
                  }}
                  exact
                  to="/account"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="user">Account</CDBSidebarMenuItem>
                </NavLink>

                {/* Updates */}
                <NavLink
                  onClick={() => {
                    handlePress("notifications");
                  }}
                  exact
                  to="/notifications"
                  activeClassName="activeClicked"
                >
                  {!retailer && <CDBSidebarMenuItem icon="bell">Update Hub</CDBSidebarMenuItem>}
                </NavLink>
                <NavLink
                  onClick={() => {
                    window.open("https://prmassist.ispring.eu/");
                  }}
                  exact
                  to="/training"
                  activeClassName="training"
                >
                  {!retailer && <CDBSidebarMenuItem icon="bell">Training</CDBSidebarMenuItem>}
                </NavLink>
              </>
            )}
          </CDBSidebarMenu>
        </CDBSidebarContent>



        {/* Absolute session timeout modal */}
        <Modal show={showTimeoutModal} centered onHide={() => setShowTimeoutModal(false)} backdrop="static">
          <Modal.Header>
            <Modal.Title style={{ fontWeight: 'bold' }}>Session Expired</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ margin: 0 }}>
              Your session has reached the maximum duration of 12 hours 30 minutes.
              For your security, you have been automatically logged out.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowTimeoutModal(false)}>OK</Button>
          </Modal.Footer>
        </Modal>

        {/* Footer */}
        <CDBSidebarFooter
          style={{ textAlign: "center" }}
          prefix={<i className="fa fa-bars fa-large"></i>}
        >
          <SidebarIcon
            icon={
              <FiLogOut
                style={{
                  marginTop: Spacing.SCALE_12,
                  textAlign: "center",
                  justifySelf: "center",
                }}
                size={32}
                color={"white"}
              />
            }
            handleClick={() => handleLogout()}
          />
        </CDBSidebarFooter>
      </CDBSidebar>
    </div>
    </>
  );
}

const mobileStyles = {
  topBar: {
    position: 'sticky',
    top: 0,
    zIndex: 1030,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    height: 56,
    padding: '0 8px',
    backgroundColor: Colors.SIDEBAR_COLOR,
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
  },
  menuButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    border: 'none',
    background: 'transparent',
    borderRadius: 8,
    cursor: 'pointer',
  },
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1040,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  drawer: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 1045,
    display: 'flex',
    maxWidth: '85vw',
    transition: 'transform 0.25s ease, visibility 0.25s',
    boxShadow: '2px 0 12px rgba(0,0,0,0.25)',
  },
};



export default Sidebar;